/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 169 lines
 * - Adding: ~12 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_CONDITION_VALIDATION_HANDLER
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Pass conditionNeedsReview flag from parser to form items. Update success
 * notification to inform user about items needing condition review.
 *
 * File Size Check:
 * - Current: 147 lines
 * - Adding: ~10 lines
 * - Projected: 157 lines
 * - Decision: Proceed (acceptable for hook utility)
 *
 * Related Screens / Modules:
 * - client/src/utils/parseBroadcastFile.ts (sets the flag)
 * - client/src/features/broadcast/BroadcastItemRow.tsx (displays badge)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_FILE_UPLOAD_HOOK
 * Date:         2025-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Extracted file upload logic from BroadcastCreatePage into a reusable hook.
 * Added support for onUploadSuccess callback to trigger form display.
 *
 * File Size Check:
 * - New hook
 * - Projected: ~100 lines
 * - Decision: Proceed (within 100-line hook limit)
 */

import React, { useRef } from "react";
import { notify } from "../../../utils/notifications";
import { parseBroadcastFile, ParsedItem } from "../../../utils/parseBroadcastFile";
import { BROADCAST_MAX_LINES } from "../../../config/environment";
import { summarizeByItem } from "../broadcast-form/utils/broadcastHelpers";
import { validationSchema } from "../broadcast-form/utils/validationSchema";
import { checkAuthStatus } from "../../../utils/authUtils";

export const useFileUpload = (formikInstance: any, onUploadSuccess?: () => void) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    const isValidExt = /\.(csv|xlsx)$/i.test(file.name);
    if (!isValidExt) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "Only Excel (.xlsx) or CSV (.csv) files can be uploaded");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (uploadButtonRef.current) {
        uploadButtonRef.current.blur();
      }
      return;
    }

    try {
      const result = await parseBroadcastFile(file);

      if (result.errors.length > 0) {
        const errorCount = result.errors.length;
        const displayErrors = result.errors.slice(0, 5);
        const errorMessage =
          displayErrors.join("\n") +
          (errorCount > 5 ? `\n...and ${errorCount - 5} more errors` : "");

        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, errorMessage);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        await handleUploadSuccess(result.items, result.warnings || []);
      }
    } catch (error) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(error, "We couldn't parse the file. Please check the format and try again");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadSuccess = async (items: ParsedItem[], warnings: string[] = []) => {
    if (!formikInstance) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "Form isn't ready yet. Please try again");
      return;
    }

    if (items.length === 0) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, 'No valid rows found. Each row needs a SKU, Model, or Part Number');
      return;
    }

    // [2026-02-13] cookie-auth fix: Get preferred currency from server (cookie-based auth)
    const userInfo = await checkAuthStatus();
    const preferredCurrency = userInfo?.preferredCurrency || "USD";

    const MAX = BROADCAST_MAX_LINES;
    const newItems = items
      .filter((r) => r.partNumber && r.partNumber.trim())
      .slice(0, MAX)
      .map((r) => ({
        categories: r.category?.trim() ? [r.category.trim()] : undefined,
        manufacturer: r.manufacturer?.trim() || "",
        partNumber: r.partNumber.trim(),
        model: "",
        // Do NOT default required fields; leave empty so UI can highlight issues immediately.
        condition: r.conditionNeedsReview ? "" : (r.condition?.trim() || ""),
        quantity: (() => {
          const n =
            typeof r.quantity === "number"
              ? r.quantity
              : r.quantity
                ? Number(r.quantity)
                : undefined;
          return typeof n === "number" && Number.isFinite(n) ? n : undefined;
        })(),
        price: (() => {
          const n =
            typeof r.price === "number"
              ? r.price
              : r.price
                ? Number(r.price)
                : undefined;
          return typeof n === "number" && Number.isFinite(n) ? n : undefined;
        })(),
        currency: preferredCurrency,
        description: r.description?.trim() || "",
        categoryNeedsReview: r.categoryNeedsReview || false,
        conditionNeedsReview: r.conditionNeedsReview || false,
      }));

    // Set values without relying on implicit async state ordering.
    // We validate the exact payload we just built, then explicitly set Formik errors/touched
    // so fields turn red immediately (no server roundtrip).
    await formikInstance.setFieldValue("broadcast", newItems, false);
    await formikInstance.setFieldValue("items", newItems, false);

    const nextValues = {
      ...(formikInstance.values || {}),
      broadcast: newItems,
      items: newItems,
    };

    const normalizeYupPath = (p: string) => p.replace(/\[(\d+)\]/g, ".$1");
    const isIndex = (s: string) => /^\d+$/.test(s);
    const setNestedLeaf = (root: any, path: string, leafValue: any) => {
      const parts = path.split(".");
      let cur = root;
      for (let i = 0; i < parts.length; i++) {
        const keyRaw = parts[i];
        const last = i === parts.length - 1;
        const next = parts[i + 1];
        const key: any = isIndex(keyRaw) ? Number(keyRaw) : keyRaw;

        if (last) {
          if (Array.isArray(cur) && typeof key === "number") cur[key] = leafValue;
          else cur[key] = leafValue;
          return;
        }

        const shouldBeArray = typeof next === "string" && isIndex(next);
        if (Array.isArray(cur) && typeof key === "number") {
          cur[key] ??= shouldBeArray ? [] : {};
          cur = cur[key];
        } else {
          cur[key] ??= shouldBeArray ? [] : {};
          cur = cur[key];
        }
      }
    };

    const broadcastOnly: Record<string, string> = {};
    try {
      await validationSchema.validate(nextValues, { abortEarly: false });
      // Clear any previous errors from earlier uploads.
      await formikInstance.setErrors({});
      await formikInstance.setTouched({ broadcast: [] }, false);
    } catch (err: any) {
      const details = Array.isArray(err?.inner) && err.inner.length > 0 ? err.inner : [err];
      details.forEach((e: any) => {
        const p = typeof e?.path === "string" ? normalizeYupPath(e.path) : "";
        const m = typeof e?.message === "string" ? e.message : "Invalid value";
        if (p.startsWith("broadcast.")) {
          broadcastOnly[p] = m;
        }
      });

      // Populate Formik errors so fields show red immediately.
      const errorsObj: any = { broadcast: [] as any[] };
      const touchedObj: any = { broadcast: [] as any[] };
      Object.entries(broadcastOnly).forEach(([p, m]) => {
        setNestedLeaf(errorsObj, p, m);
        setNestedLeaf(touchedObj, p, true);
      });
      await formikInstance.setErrors(errorsObj);
      await formikInstance.setTouched(touchedObj, false);

      // Scroll to first failing field (nice UX for large uploads)
      const firstPath = Object.keys(broadcastOnly)[0];
      if (firstPath) {
        const el = document.querySelector(`[data-field-path="${firstPath}"]`) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as any)?.focus?.();
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    const categoriesNeedingReview = newItems.filter((item) => item.categoryNeedsReview).length;
    const conditionsNeedingReview = newItems.filter((item) => item.conditionNeedsReview).length;
    const itemsNeedingReview = categoriesNeedingReview + conditionsNeedingReview;
    const totalItems = items.length;
    const loadedItems = newItems.length;
    const message = totalItems > MAX 
      ? `Loaded ${loadedItems} item(s). ${totalItems - MAX} item(s) not loaded (max ${MAX} per broadcast).`
      : `Loaded ${loadedItems} item(s).`;

    const validationLines = summarizeByItem(broadcastOnly);
    const attentionCount = validationLines.length;

    // Avoid showing multiple toasts (success + warning) for the same upload.
    // Show success only when clean; otherwise show a single warning with the summary.
    if (itemsNeedingReview > 0 || attentionCount > 0 || (warnings?.length ?? 0) > 0) {
      const parts: string[] = [message];

      if (itemsNeedingReview > 0) {
        if (categoriesNeedingReview > 0 && conditionsNeedingReview > 0) {
          parts.push(
            `${categoriesNeedingReview} item(s) need category review and ${conditionsNeedingReview} item(s) need condition review.`
          );
        } else if (categoriesNeedingReview > 0) {
          parts.push(`${categoriesNeedingReview} item(s) need category review.`);
        } else if (conditionsNeedingReview > 0) {
          parts.push(`${conditionsNeedingReview} item(s) need condition review.`);
        }
      }

      if (attentionCount > 0) {
        parts.push(`${attentionCount} item(s) need attention:\n${validationLines.join("\n")}`);
      }

      // NOTE: row-level warnings can be redundant vs field highlights; keep them out of the main toast for clarity.
      notify.warning(parts.join("\n"));
    } else {
      notify.success(message);
    }

    // [2025-01-01] Empty state: Show form after successful upload
    if (onUploadSuccess) {
      onUploadSuccess();
    }
  };

  return {
    fileInputRef,
    uploadButtonRef,
    handleUploadClick,
    handleFileChange,
  };
};
